import deviceApi from '@api/deviceApi';
import locationApi from '@api/locationApi';
import DefaultPage from '@components/defaultPage';
import { LoadingIndicator } from '@components/loadingIndicator';
import useCurrentLocation from '@hooks/useCurrentLocation';
import useLastDefined from '@hooks/useLastDefined';
import { t } from '@i18n';
import { Alert } from '@mantine/core';
import CreateDevice from '@routes/inventory/$deviceType/$deviceId/-createDevice';
import ViewDevice from '@routes/inventory/$deviceType/$deviceId/-viewDevice';
import { createFileRoute } from '@tanstack/react-router';
import { useHandleError } from '@util/errors';
import { useEffect } from 'react';

export const Route = createFileRoute('/inventory/$deviceType/$deviceId/')({
  component: DevicePage,
});

function DevicePage() {
  const { deviceType, deviceId } = Route.useParams();
  const { locationId = '' } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { state } = useCurrentLocation();

  const [globalDevice, globalDeviceError] = deviceApi
    .getGlobal(deviceType, deviceId)
    .useCache({ loadingBoundary: true });

  const [_localDevice, deviceError] = deviceApi
    .get(locationId, deviceId)
    .useCache({ loadingBoundary: true, disabled: !locationId });
  const localDevice = useLastDefined(_localDevice);

  const [deviceLocation] = locationApi
    .get(globalDevice?.location?.locationId ?? '')
    .useCache({ disabled: !globalDevice?.location?.locationId });
  useHandleError([globalDeviceError, deviceError]);

  const [localLocation] = locationApi.get(locationId).useCache({ disabled: !locationId });

  useEffect(() => {
    // If users can see the device's sequence number, we can assume they have access to the location
    // and can navigate to it
    if (!locationId && globalDevice?.location?.sequenceNumber && deviceLocation) {
      navigate({ search: { locationId: deviceLocation.id } });
    }
  }, [locationId, globalDevice, deviceLocation, navigate]);

  if (
    globalDevice === undefined ||
    (locationId && localDevice === undefined) ||
    (!locationId && globalDevice?.location?.sequenceNumber && deviceLocation)
  ) {
    return (
      <DefaultPage backButton={{ to: '/inventory' }}>
        <LoadingIndicator size="grow" label={t('device.lookingUp')} css={{ justifySelf: 'start' }} />
      </DefaultPage>
    );
  }

  if (globalDevice === null) {
    return (
      <DefaultPage backButton={{ to: '/inventory' }}>
        <Alert title={t('device.notFound', { id: deviceId })} color="red" />
      </DefaultPage>
    );
  }

  if (!locationId) {
    return (
      <DefaultPage backButton={{ to: '/inventory' }}>
        <Alert title={t('device.mustSelectLocation')} />
      </DefaultPage>
    );
  }

  if (!localDevice && !state.fromScanner) {
    return (
      <DefaultPage backButton={{ to: '/inventory' }}>
        <Alert title={t('device.scanToRegister', { location: localLocation?.name ?? locationId })} />
      </DefaultPage>
    );
  }

  if (!localDevice) {
    return <CreateDevice />;
  }

  return <ViewDevice device={localDevice} />;
}
